import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core'
import { trigger, state, style, transition, animate } from '@angular/animations'
import { UiService } from 'src/app/services/ui.service'
import { AccessScope, AccessState } from 'src/app/services/access.service'
import { CopyFeedbackService } from 'src/app/shared/copy-feedback.service'

/**
 * Shared animation trigger for access view components.
 * Import this in your component's animations array.
 */
export const slideDownAnimation = trigger('slideDown', [
  state('collapsed', style({ height: '0', overflow: 'hidden' })),
  state('expanded', style({ height: '*', overflow: 'visible' })),
  transition('collapsed <=> expanded', [animate('200ms ease-in-out')]),
])

/**
 * Base class for AWS and Azure access view components.
 * Contains shared logic for the card UI pattern.
 */
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export abstract class AccessViewBaseComponent implements OnChanges {
  @Input() state: AccessState
  @Input() accessType: AccessScope
  @Input() region: string
  @Input() completedTestCount: number
  // Access works but the deployed template is behind. Distinct from AccessState,
  // which lumps out-of-date in with invalid-config under UPDATE_TEMPLATE.
  @Input() connectedOnOlderTemplate = false

  @Output() buttonClicked = new EventEmitter<null>()

  expanded = true // Start expanded by default

  constructor(
    public uiService: UiService,
    public copyFeedback: CopyFeedbackService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Auto-collapse when access test succeeds
    if (changes['state'] && this.state === AccessState.SUCCESS) {
      this.expanded = false
    }
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded
  }

  onCopied(): void {
    this.copyFeedback.onCopied('default')
  }

  get copied(): boolean {
    return !!this.copyFeedback.copiedStates['default']
  }

  testButtonClick(): void {
    this.buttonClicked.emit(null)
  }

  get retryText(): string {
    const textOptions = ["It's still not working.", "Hmm... It's not working yet."]
    const testCount = this.completedTestCount
    return testCount ? textOptions[testCount % textOptions.length] : ''
  }

  get successfullyConnected(): boolean {
    return this.state === AccessState.SUCCESS
  }

  get testing(): boolean {
    return this.state === AccessState.TESTING
  }

  get connectSuccess(): boolean {
    return this.state === AccessState.SUCCESS || this.state === AccessState.UPDATE_TEMPLATE
  }

  get title(): string {
    switch (this.accessType) {
      case AccessScope.SOURCE:
        return 'Primary Environment'
      case AccessScope.TARGET:
        return 'Recovery Environment'
      case AccessScope.FAILBACK:
        return 'Primary Environment Failback Access'
    }
    return 'Unknown access type'
  }
}
